import { Box, Button, Flex, Icon, Table, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEnvelope, FaEye } from "react-icons/fa";
import {
  BseDto,
  EmailInviteDto,
  OrganizationInviteStatus,
  OrganizationType,
  PersonDto,
  useCreateEmailInviteReminderMutation,
  useCreateOrganizationInviteEmailReminderMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { displayPersonName } from "../../../contracts/view-single/sharing/AddExternalParticipantModal";
import { requireDeadlineHasPassed } from "../../requireDeadlineHasPassed";
import { EmaiInviteStatusTag } from "./EmailInviteStatusTag";
import { EmployeeInfo } from "./EmployeeInfo";
import { InvitedOrganizationStatusTag } from "./InvitedOrganizationStatusTag";
import { useNegotiation } from "./NegotiationContext";
import { OrganizationCell } from "./OrganizationCell";
import { ViewBidAsBuyerModal } from "./ViewBidAsBuyerModal";

interface Props {
  event: BseDto;
}

export interface CombinedInvite {
  organizationName: string;
  organizationId?: string | null;
  type: OrganizationType;
  registeredUsers: PersonDto[];
  emailInvites: EmailInviteDto[];
  status?: OrganizationInviteStatus | null;
  bidId?: string | null;
  organizationInviteId?: string | null;
}

export const InvitedOrganizations = ({ event }: Props) => {
  const [orgIdToView, setOrgIdToView] = useState<string>();
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(null);
  const { t } = useTranslation();
  const toast = useToast();
  const authState = useLoggedInWithOrgContextState();
  const { currentRound } = useNegotiation();

  const [sendOrgEntryReminder] = useCreateEmailInviteReminderMutation();
  const [sendOrgReminder] = useCreateOrganizationInviteEmailReminderMutation();

  const deadlineHasPassed = requireDeadlineHasPassed(event, currentRound);

  const orgInvites = useMemo(
    () => (currentRound ? currentRound.invites : event.organizationInvites),
    [currentRound, event.organizationInvites]
  );

  const groupedOrgEntryInvites = useMemo(() => {
    const invites: { [entryId: string]: EmailInviteDto[] } = {};
    const emailInvites = currentRound ? [] : event.emailInvites;

    emailInvites.forEach((invite) => {
      const invitesForOrgEntry = invites[invite.organizationEntry?.id ?? ""] ?? [];
      invites[invite.organizationEntry?.id ?? ""] = [...invitesForOrgEntry, invite];
    });
    return invites;
  }, [event.emailInvites, currentRound]);

  const combinedInvites: CombinedInvite[] = useMemo(() => {
    const organizationInvites: CombinedInvite[] = orgInvites.map((invite) => {
      return {
        organizationName: invite.organizationName,
        organizationId: invite.organizationId,
        type: OrganizationType.Organization,
        registeredUsers: invite.persons,
        emailInvites: "emailInvites" in invite ? invite.emailInvites : [],
        status: invite.status,
        bidId: invite.bidId,
        organizationInviteId: invite.id,
      };
    });

    const organizationEntryInvites: CombinedInvite[] = Object.values(groupedOrgEntryInvites).map((emailInvites) => ({
      organizationName: emailInvites[0].organizationEntry?.name ?? `${t("No organization")}`,
      type: OrganizationType.OrganizationEntry,
      registeredUsers: [],
      emailInvites: emailInvites,
    }));

    return [...organizationInvites, ...organizationEntryInvites];
  }, [orgInvites, groupedOrgEntryInvites, t]);

  return (
    <Box
      width="full"
      border="1px solid"
      borderColor="smBorder"
      borderRadius="md"
      overflowX={{ base: "auto", md: "visible" }}
      py="2.5"
    >
      {orgIdToView && (
        <ViewBidAsBuyerModal
          eventId={event.id}
          organizationId={orgIdToView}
          onClose={() => setOrgIdToView(undefined)}
        />
      )}
      <Table variant="simple" size={"sm"}>
        <Thead>
          <Tr>
            <Th>{t("Company")}</Th>
            <Th>{t("Invited persons")}</Th>
            <Th textAlign="center">{t("Status")}</Th>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {combinedInvites.length === 0 && (
            <Tr>
              <Td colSpan={4} width="full" textAlign="center">
                {t("No invitations")}
              </Td>
            </Tr>
          )}
          {combinedInvites.map((invite) => {
            const totalRows = invite.registeredUsers.length + invite.emailInvites.length;
            const rowSpan = Math.max(1, totalRows);

            return (
              <Fragment key={`${invite.organizationId}-${invite.type}`}>
                {/* Registered Users Section */}
                {invite.registeredUsers.length > 0 &&
                  invite.registeredUsers.map((user, userIndex) => (
                    <Tr key={`${invite.organizationId}-${invite.type}-registered-${user.email}`}>
                      {/* Only render organization cell for the first row */}
                      {userIndex === 0 && <OrganizationCell invite={invite} rowSpan={rowSpan} />}
                      <Td>
                        {userIndex === 0 && (
                          <Text fontSize="sm" fontWeight="semibold" color="smMuted" mb="2">
                            {t("Registered users")}
                          </Text>
                        )}
                        <EmployeeInfo
                          fullName={displayPersonName(user)}
                          email={user.email}
                          phoneNumber={user.phoneNumber ?? null}
                          corporateTitle={user.corporateTitle ?? null}
                        />
                      </Td>
                      {userIndex === 0 && (
                        <Td verticalAlign="top" rowSpan={invite.registeredUsers.length}>
                          {invite.status && (
                            <Flex justifyContent="center" pt="6">
                              <InvitedOrganizationStatusTag status={invite.status} />
                            </Flex>
                          )}
                        </Td>
                      )}
                      {userIndex === 0 && (
                        <Td verticalAlign="top" rowSpan={invite.registeredUsers.length}>
                          {invite.bidId &&
                            (deadlineHasPassed || authState.selectedOrg.canSeeDeliveredBidsBeforeDeadline) && (
                              <Flex justifyContent="center" pt="6">
                                <Button
                                  leftIcon={<Icon as={FaEye} w="13px" h="13px" />}
                                  w="100px"
                                  variant={"solid"}
                                  colorScheme="teal"
                                  size={"sm"}
                                  onClick={() => invite.organizationId && setOrgIdToView(invite.organizationId)}
                                >
                                  {t("View bid")}
                                </Button>
                              </Flex>
                            )}
                        </Td>
                      )}
                    </Tr>
                  ))}

                {/* Email Invites Section */}
                {invite.emailInvites.length > 0 && (
                  <>
                    {/* Organization status row for email-only invites */}
                    {invite.registeredUsers.length === 0 && (
                      <Tr key={`${invite.organizationId}-${invite.type}-org-status`}>
                        <OrganizationCell invite={invite} rowSpan={invite.emailInvites.length + 1} />
                        <Td></Td>
                        <Td textAlign="center">
                          <Flex justifyContent="center">
                            <InvitedOrganizationStatusTag
                              status={invite.status ?? OrganizationInviteStatus.InvitationSent}
                            />
                          </Flex>
                        </Td>
                        <Td verticalAlign="top">
                          {invite.bidId &&
                            (deadlineHasPassed || authState.selectedOrg.canSeeDeliveredBidsBeforeDeadline) && (
                              <Flex justifyContent="center" pt="6">
                                <Button
                                  leftIcon={<Icon as={FaEye} w="13px" h="13px" />}
                                  w="100px"
                                  variant={"solid"}
                                  colorScheme="teal"
                                  size={"sm"}
                                  onClick={() => invite.organizationId && setOrgIdToView(invite.organizationId)}
                                >
                                  {t("View bid")}
                                </Button>
                              </Flex>
                            )}
                        </Td>
                      </Tr>
                    )}
                    {/* Email invite rows */}
                    {invite.emailInvites.map((emailInvite, emailIndex) => (
                      <Tr key={`${invite.organizationId}-${invite.type}-email-${emailInvite.email}`}>
                        <Td>
                          {emailIndex === 0 && (
                            <Text fontSize="sm" fontWeight="semibold" color="smMuted" mb="2">
                              {t("Email invites")}
                            </Text>
                          )}
                          <EmployeeInfo email={emailInvite.email} phoneNumber={null} corporateTitle={null} />
                        </Td>
                        <Td textAlign="center" verticalAlign="bottom">
                          <EmaiInviteStatusTag status={emailInvite.status} size="sm" />
                        </Td>
                        <Td verticalAlign="bottom" textAlign="center">
                          <Button
                            size="xs"
                            variant="outline"
                            rightIcon={<Icon as={FaEnvelope} />}
                            py="15px"
                            onClick={async () => {
                              setSendingReminderId(emailInvite.id);
                              try {
                                if (invite.type === OrganizationType.Organization && invite.organizationInviteId) {
                                  await sendOrgReminder({
                                    eventId: event.id,
                                    inviteId: invite.organizationInviteId,
                                    emailInviteId: emailInvite.id,
                                  }).unwrap();
                                } else if (invite.type === OrganizationType.OrganizationEntry) {
                                  await sendOrgEntryReminder({
                                    eventId: event.id,
                                    inviteId: emailInvite.id,
                                  }).unwrap();
                                } else {
                                  throw new Error("Invalid invite type");
                                }
                                toast({
                                  title: t("Reminder sent"),
                                  status: "success",
                                });
                              } catch (error) {
                                toast({
                                  title: t("Could not send reminder"),
                                  status: "error",
                                });
                              } finally {
                                setSendingReminderId(null);
                              }
                            }}
                            isLoading={sendingReminderId === emailInvite.id}
                          >
                            {t("Send reminder")}
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </>
                )}

                {/* Empty state row when no registered users and no email invites */}
                {invite.registeredUsers.length === 0 && invite.emailInvites.length === 0 && (
                  <Fragment>
                    <Tr key={`${invite.organizationId}-${invite.type}-empty`}>
                      <OrganizationCell invite={invite} rowSpan={2} />
                      <Td></Td>
                      <Td textAlign="center">
                        {invite.status && (
                          <Flex justifyContent="center" pt="6">
                            <InvitedOrganizationStatusTag status={invite.status} />
                          </Flex>
                        )}
                      </Td>
                      <Td verticalAlign="top">
                        {invite.bidId &&
                          (deadlineHasPassed || authState.selectedOrg.canSeeDeliveredBidsBeforeDeadline) && (
                            <Flex justifyContent="center" pt="6">
                              <Button
                                leftIcon={<Icon as={FaEye} w="13px" h="13px" />}
                                w="100px"
                                variant={"solid"}
                                colorScheme="teal"
                                size={"sm"}
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                onClick={() => invite.organizationId && setOrgIdToView(invite.organizationId!)}
                              >
                                {t("View bid")}
                              </Button>
                            </Flex>
                          )}
                      </Td>
                    </Tr>
                  </Fragment>
                )}
              </Fragment>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
